/**
 * Team Service
 *
 * Handles team management operations via the Team_Service Lambda.
 * Admin-only endpoints for CRUD on teams.
 */

import apiClient from './api';

/**
 * Get list of teams
 * Admin sees all; Manager/Viewer sees only teams in Authorised_Team_Set.
 */
export const getTeams = async (params = {}) => {
  try {
    const { status } = params;
    const queryParams = {};
    if (status) queryParams.status = status;

    const response = await apiClient.get('/teams', {
      params: queryParams,
      skipActiveTeamHeader: true
    });

    const apiData = response.data?.data || response.data;
    const teamList = apiData?.teams || [];

    return {
      data: teamList.map(team => ({
        team_id: team.team_id || team.TEAM_ID,
        name: team.name || team.NAME,
        description: team.description || team.DESCRIPTION || '',
        source_account_id: team.source_account_id || team.SOURCE_ACCOUNT_ID || '',
        contact_email: team.contact_email || team.CONTACT_EMAIL || '',
        status: team.status || team.STATUS || 'active',
        is_default: team.is_default || team.IS_DEFAULT || false,
        trigger_bucket: team.trigger_bucket || team.TRIGGER_BUCKET || '',
        trigger_prefix: team.trigger_prefix || team.TRIGGER_PREFIX || '',
        created_at: team.created_at || team.CREATED_AT || '',
        created_by: team.created_by || team.CREATED_BY || ''
      }))
    };
  } catch (error) {
    console.error('Failed to fetch teams:', error);
    throw error;
  }
};

/**
 * Get a single team by team_id
 */
export const getTeamById = async (teamId) => {
  try {
    const response = await apiClient.get(`/teams/${teamId}`, {
      skipActiveTeamHeader: true
    });

    const team = response.data?.data || response.data;

    return {
      team_id: team.team_id || team.TEAM_ID,
      name: team.name || team.NAME,
      description: team.description || team.DESCRIPTION || '',
      source_account_id: team.source_account_id || team.SOURCE_ACCOUNT_ID || '',
      contact_email: team.contact_email || team.CONTACT_EMAIL || '',
      status: team.status || team.STATUS || 'active',
      is_default: team.is_default || team.IS_DEFAULT || false,
      trigger_bucket: team.trigger_bucket || team.TRIGGER_BUCKET || '',
      trigger_prefix: team.trigger_prefix || team.TRIGGER_PREFIX || '',
      created_at: team.created_at || team.CREATED_AT || '',
      created_by: team.created_by || team.CREATED_BY || ''
    };
  } catch (error) {
    console.error('Failed to fetch team:', error);
    throw error;
  }
};

/**
 * Create a new team
 */
export const createTeam = async (teamData) => {
  try {
    const response = await apiClient.post('/teams', {
      team_id: teamData.team_id,
      name: teamData.name,
      description: teamData.description || '',
      source_account_id: teamData.source_account_id,
      contact_email: teamData.contact_email
    }, {
      skipActiveTeamHeader: true
    });

    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to create team:', error);
    throw error;
  }
};

/**
 * Update a team (mutable fields only: name, description, contact_email)
 */
export const updateTeam = async (teamId, teamData) => {
  try {
    const payload = {};
    if (teamData.name !== undefined) payload.name = teamData.name;
    if (teamData.description !== undefined) payload.description = teamData.description || '';
    if (teamData.contact_email !== undefined) payload.contact_email = teamData.contact_email;
    if (teamData.source_account_id !== undefined) payload.source_account_id = teamData.source_account_id;

    const response = await apiClient.put(`/teams/${teamId}`, payload, {
      skipActiveTeamHeader: true
    });

    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to update team:', error);
    throw error;
  }
};

/**
 * Disable a team
 */
export const disableTeam = async (teamId) => {
  try {
    const response = await apiClient.post(`/teams/${teamId}/disable`, {}, {
      skipActiveTeamHeader: true
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to disable team:', error);
    throw error;
  }
};

/**
 * Re-enable a disabled team
 */
export const enableTeam = async (teamId) => {
  try {
    const response = await apiClient.put(`/teams/${teamId}`, {
      status: 'active'
    }, {
      skipActiveTeamHeader: true
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to enable team:', error);
    throw error;
  }
};

/**
 * Delete a team (blocked if is_default = true)
 */
export const deleteTeam = async (teamId) => {
  try {
    await apiClient.delete(`/teams/${teamId}`, {
      skipActiveTeamHeader: true
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to delete team:', error);
    throw error;
  }
};

/**
 * Get the bucket policy snippet for a team (copy/paste for the source account admin)
 */
export const getBucketPolicySnippet = async (teamId) => {
  try {
    const response = await apiClient.get(`/teams/${teamId}/bucket-policy-snippet`, {
      skipActiveTeamHeader: true
    });
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to fetch bucket policy snippet:', error);
    throw error;
  }
};
